import { theme } from 'antd';
import {
	ChangeUserCategoryIcon,
	DeletedFilled,
	DisableIcon,
	EnableIcon,
	KeyIcon,
	RevokeAccessIcon,
	TeamIcon,
	UserIcon
} from 'assets/images/svg';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { useManagePermittedRoles, useModuleSettings } from 'library/hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import {
	ChangePersonType,
	DeletePeopleModal,
	DisablePeopleModal,
	EnablePeopleModal,
	GrantPersonPermissionsModal,
	ManageAssociatedPeopleModal,
	ManageTeamsModal
} from 'zComponents/molecules';
import { Button, Divider, Dropdown, Flex, Tooltip } from 'zui/Antd';

const ManagePersonDropdown = ({ personID, isTransparent = false }) => {
	// ✅ Hooks
	const {
		token: {
			colorBgContainer,
			boxShadow,
			borderRadiusLG,
			paddingSM,
			fontSizeLG,
			colorPrimaryText,
			fontWeightStrong
		}
	} = theme.useToken();
	const dispatch = useDispatch();
	const enabledInnerRangeModule = useModuleSettings(CONSTANTS_SETTINGS.INNER_RANGE_MODULE);

	// ✅ Redux States
	const person = useSelector((state) => selectPeopleByPersonID(state, personID));
	const { loadingResetPassword } = useSelector((state) => state.People);

	// ✅ Local State
	const [openDropdown, setOpenDropdown] = useState(false);

	//✅ Permissions
	const canManagePermissions = useManagePermittedRoles('aCPermissionManagement');
	const canManageMembers = useManagePermittedRoles('manageMembers');
	const canManageAssociatedPeople = useManagePermittedRoles('manageAssociatedPeople');
	const canEnablePerson = useManagePermittedRoles('enablePerson');
	const canDisablePerson = useManagePermittedRoles('disablePerson');
	const canDeletePerson = useManagePermittedRoles('deletePerson');
	const canResetPasswordPerson = useManagePermittedRoles('resetPasswordPerson');
	const canChangeUserType = useManagePermittedRoles('changeUserType');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const items = [
		{
			key: 'Manage Permissions',
			label: (
				<GrantPersonPermissionsModal personID={personID}>
					<Button
						type='text'
						icon={<EnableIcon width={16} height={16} color='currentColor' />}
						block
						disabled={!canManagePermissions}
						style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
					>
						Manage Permissions
					</Button>
				</GrantPersonPermissionsModal>
			)
		},
		{
			key: 'Manage Teams',
			label: (
				<ManageTeamsModal personID={personID}>
					<Button
						type='text'
						icon={<TeamIcon color='currentColor' />}
						block
						disabled={!canManageMembers}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Manage Teams
					</Button>
				</ManageTeamsModal>
			)
		},
		{
			key: 'Send Password Reset',
			label: (
				<Tooltip
					title={!canResetPasswordPerson ? permissionDeniedMessage : "Reset this Person's Password"}
					placement='top'
				>
					<Button
						type='text'
						icon={<KeyIcon color='currentColor' />}
						block
						onClick={(e) => {
							e.stopPropagation();
							if (!canResetPasswordPerson) return;
							dispatch(
								peopleActions.resetPersonPassword({
									email: person.email
								})
							);
							setOpenDropdown(false);
						}}
						loading={loadingResetPassword}
						disabled={!canResetPasswordPerson}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Send Password Reset
					</Button>
				</Tooltip>
			)
		},
		{
			key: 'Change User Category',
			label: (
				<ChangePersonType personID={personID}>
					<Button
						type='text'
						icon={<ChangeUserCategoryIcon color='currentColor' />}
						block
						disabled={person?.fBPersonID || !canChangeUserType}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Change User Category
					</Button>
				</ChangePersonType>
			)
		},
		...(enabledInnerRangeModule
			? [
					{
						key: 'Manage Associated People',
						label: (
							<ManageAssociatedPeopleModal personID={personID}>
								<Button
									type='text'
									icon={<UserIcon color='currentColor' />}
									block
									disabled={person?.masterUserID || !canManageAssociatedPeople}
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Manage Associated People
								</Button>
							</ManageAssociatedPeopleModal>
						)
					}
				]
			: []),
		{
			key: 'divider',
			label: <Divider style={{ margin: 0 }} />
		},
		{
			key: 'Revoke All Access',
			label: (
				<Tooltip title='Currently working on it' placement='top'>
					<Button
						type='text'
						icon={<RevokeAccessIcon color='currentColor' />}
						block
						disabled
						style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
					>
						Revoke All Access
					</Button>
				</Tooltip>
			)
		},
		...(person?.isActive
			? [
					{
						key: 'Disable Profile',
						label: (
							<DisablePeopleModal personIDs={[personID]}>
								<Button
									type='text'
									icon={<DisableIcon color='currentColor' width={16} height={16} />}
									block
									disabled={!canDisablePerson}
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Disable Profile
								</Button>
							</DisablePeopleModal>
						)
					}
				]
			: [
					{
						key: 'Enable Profile',
						label: (
							<EnablePeopleModal personIDs={[personID]}>
								<Button
									type='text'
									icon={<EnableIcon color='currentColor' width={fontSizeLG} height={fontSizeLG} />}
									block
									disabled={!canEnablePerson}
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Enable Profile
								</Button>
							</EnablePeopleModal>
						)
					}
				]),
		{
			key: 'Delete Profile',
			label: (
				<DeletePeopleModal personIDs={[personID]}>
					<Button
						type='text'
						danger
						icon={<DeletedFilled width={16} height={16} color='currentColor' />}
						block
						disabled={!canDeletePerson}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Delete Profile
					</Button>
				</DeletePeopleModal>
			)
		}
	];

	return (
		<Dropdown
			trigger={['click']}
			placement='bottomLeft'
			open={openDropdown}
			onOpenChange={(visible) => setOpenDropdown(visible)}
			dropdownRender={() => (
				<Flex
					style={{
						backgroundColor: colorBgContainer,
						borderRadius: borderRadiusLG,
						width: 240,
						boxShadow: boxShadow,
						padding: paddingSM,
						position: 'relative'
					}}
					vertical
					gap={paddingSM}
				>
					{items.map((item) => (
						<div key={item.key} onClick={() => setOpenDropdown(false)}>
							{item.label}
						</div>
					))}
				</Flex>
			)}
		>
			<Button type={isTransparent ? 'text' : 'primary'} style={{ color: isTransparent && colorPrimaryText }}>
				{isTransparent ? 'View' : 'Manage'} Person
			</Button>
		</Dropdown>
	);
};

export default ManagePersonDropdown;
